import { ArticleReactionOptions } from "@elements/Reactions/ArticleReactionOptions";
import { ArticleReactions } from "@elements/Reactions/ArticleReactions";
import { isArrayWithElements } from "@helper/utils";
import { HandleReactionClick, ReactionProp } from "@typings/Reactions.d";
import { forwardRef, type ReactElement } from "react";

import useReactionToolbar from "./hooks/useReactionToolbar";

export type ReactionToolbarProps = {
  articleId: string;
  showReactions?: boolean;
  reactions: ReactionProp[];
  userReactions: string;
  reactionsError: Error | null;
  handleReactionClick: HandleReactionClick;
  variant?: "default" | "small";
  onExpand?: (articleId: string | null) => void;
  isExpanded?: boolean;
  qnStyle?: boolean;
};

const ReactionToolbar = forwardRef<HTMLDivElement, ReactionToolbarProps>(
  function ReactionToolbar(
    {
      articleId,
      reactions,
      userReactions,
      reactionsError,
      handleReactionClick,
      showReactions = false,
      variant = "default",
      isExpanded = false,
      onExpand,
      qnStyle = false,
    }: ReactionToolbarProps,
    ref,
  ): ReactElement {
    const { isReactionExpanded, toggleExpand, handleReactionSelect } =
      useReactionToolbar({
        articleId,
        handleReactionClick,
        isExpanded,
        onExpand,
      });

    if (!isArrayWithElements(reactions)) {
      showReactions = false;
    }

    const isSmallVariant = variant === "small";
    const floatStyle = isSmallVariant
      ? "left-0 top-9"
      : "top-12 right-0 lg:-right-20";

    return (
      <div
        className="interaction-toolbar flex"
        ref={ref}
        data-testid={`reaction-toolbar-${articleId}`}
      >
        <div className="relative flex gap-x-md md:gap-x-lg">
          {showReactions && !reactionsError && (
            <>
              <div className="items-center">
                <button
                  className="flex items-center gap-x-sm"
                  onClick={toggleExpand}
                >
                  <ArticleReactions
                    reactions={reactions}
                    reactionsUser={userReactions}
                    referer={`article-${articleId}`}
                    variant={variant}
                    {...(isSmallVariant ? { limit: 1 } : {})}
                    qnStyle={qnStyle}
                  />
                </button>
              </div>
              {isReactionExpanded && (
                <div
                  className={`absolute z-10 ${qnStyle ? "-top-20 right-2 max-sm:-right-11" : floatStyle}`}
                >
                  <ArticleReactionOptions
                    reactions={reactions}
                    handleReactionClick={handleReactionSelect}
                    reactionsUser={userReactions}
                    articleId={articleId}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  },
);

export default ReactionToolbar;
